<script lang="ts">
  import { onMount } from 'svelte';
  import {
    createOrganization,
    deleteOrganization,
    listOrganizations,
  } from '@shared/api/admin';
  import type { Organization } from '@shared/types/admin';
  import { sessionStore } from '@features/auth/session/model/store.svelte';
  import { canManageTenant } from '@shared/types/session';
  import { navigate } from '@shared/router/navigation';

  let items = $state<Organization[]>([]);
  let loading = $state(false);
  let error = $state<string | null>(null);

  let showCreate = $state(false);
  let submitting = $state(false);
  let formError = $state<string | null>(null);
  let nameInput = $state('');
  let displayNameInput = $state('');
  let descriptionInput = $state('');

  // 組織の作成/削除は AdminManageGlobal = super_admin 専用。
  let allowEdit = $derived(!!sessionStore.user && canManageTenant(sessionStore.user.role));

  async function refresh() {
    loading = true;
    error = null;
    try {
      items = await listOrganizations();
    } catch (err) {
      error = err instanceof Error ? err.message : String(err);
    } finally {
      loading = false;
    }
  }

  onMount(() => {
    void refresh();
  });

  function openCreate() {
    showCreate = true;
    nameInput = '';
    displayNameInput = '';
    descriptionInput = '';
    formError = null;
  }

  function closeCreate() {
    if (submitting) return;
    showCreate = false;
    formError = null;
  }

  async function handleCreate(ev: SubmitEvent) {
    ev.preventDefault();
    if (submitting) return;
    submitting = true;
    formError = null;
    try {
      await createOrganization({
        name: nameInput.trim(),
        display_name: displayNameInput.trim(),
        description: descriptionInput.trim(),
        roles: [],
      });
      showCreate = false;
      await refresh();
    } catch (err) {
      formError = err instanceof Error ? err.message : String(err);
    } finally {
      submitting = false;
    }
  }

  async function handleDelete(org: Organization) {
    if (!org.id) return;
    const ok = window.confirm(
      `組織 "${org.display_name || org.name}" を削除します。\n所属メンバーは自動で解除されますがユーザーは残ります。よろしいですか？`,
    );
    if (!ok) return;
    try {
      await deleteOrganization(org.id);
      await refresh();
    } catch (err) {
      window.alert(err instanceof Error ? err.message : String(err));
    }
  }

  function openDetail(org: Organization) {
    if (!org.id) return;
    navigate(`/admin/organizations/${org.id}`);
  }
</script>

<header class="settings-header">
  <p class="eyebrow">管理 / Auth0</p>
  <h1>組織</h1>
</header>

<section class="panel">
  <div class="panel-head">
    <p class="muted">
      Auth0 上の Organization を管理します。組織は SSO ログイン時の所属の単位で、
      メンバー・招待・ロールがそれぞれの組織にひもづきます。
    </p>
    <button
      type="button"
      class="btn btn-primary"
      disabled={!allowEdit}
      title={allowEdit ? '' : '権限がありません'}
      onclick={openCreate}
    >
      + 組織を追加
    </button>
  </div>

  {#if loading && items.length === 0}
    <p class="muted">読み込み中…</p>
  {:else if error}
    <div class="error">{error}</div>
  {:else if items.length === 0}
    <p class="muted">組織がまだ登録されていません。</p>
  {:else}
    <table class="data-table">
      <thead>
        <tr>
          <th>name</th>
          <th>display name</th>
          <th>roles</th>
          <th class="actions">操作</th>
        </tr>
      </thead>
      <tbody>
        {#each items as org (org.id ?? org.name)}
          <tr>
            <td><code>{org.name}</code></td>
            <td>{org.display_name}</td>
            <td>
              {#if org.roles.length === 0}
                <span class="muted">—</span>
              {:else}
                {org.roles.join(', ')}
              {/if}
            </td>
            <td class="actions">
              <button type="button" class="btn btn-link" onclick={() => openDetail(org)}>
                詳細
              </button>
              <button
                type="button"
                class="btn btn-link danger"
                disabled={!allowEdit}
                onclick={() => handleDelete(org)}
              >
                削除
              </button>
            </td>
          </tr>
        {/each}
      </tbody>
    </table>
  {/if}
</section>

{#if showCreate}
  <div class="modal-backdrop">
    <button
      type="button"
      class="modal-dismiss"
      aria-label="閉じる"
      onclick={closeCreate}
    ></button>
    <div class="modal-card" role="dialog" aria-modal="true">
      <h2>組織を追加</h2>
      <form onsubmit={handleCreate}>
        <label>
          <span>name <em>*</em></span>
          <input
            bind:value={nameInput}
            placeholder="lp-crm-acme"
            pattern="[a-z0-9_-]+"
            minlength="3"
            maxlength="50"
            required
          />
          <small class="hint">3〜50 文字、小文字英数字 / - / _ のみ</small>
        </label>
        <label>
          <span>display name <em>*</em></span>
          <input bind:value={displayNameInput} placeholder="Acme Corp." required />
        </label>
        <label>
          <span>description</span>
          <textarea bind:value={descriptionInput} rows="3"></textarea>
        </label>
        {#if formError}
          <div class="error">{formError}</div>
        {/if}
        <div class="modal-actions">
          <button type="button" class="btn" disabled={submitting} onclick={closeCreate}>
            キャンセル
          </button>
          <button type="submit" class="btn btn-primary" disabled={submitting}>
            {submitting ? '作成中…' : '作成'}
          </button>
        </div>
      </form>
    </div>
  </div>
{/if}

<style>
  .settings-header {
    margin-bottom: 16px;
  }
  .eyebrow {
    margin: 0 0 2px;
    color: var(--muted);
    font-weight: 700;
  }
  .panel {
    padding: 20px;
  }
  .panel-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 16px;
  }
  .panel-head p {
    margin: 0;
  }
  .data-table {
    width: 100%;
    border-collapse: collapse;
  }
  .data-table th,
  .data-table td {
    text-align: left;
    padding: 8px 12px;
    border-bottom: 1px solid var(--border);
  }
  .data-table th {
    font-size: 12px;
    color: var(--muted);
    font-weight: 600;
  }
  .actions {
    text-align: right;
    white-space: nowrap;
  }
  .danger {
    color: var(--danger, #c83232);
  }
  .modal-backdrop {
    position: fixed;
    inset: 0;
    display: grid;
    place-items: center;
    z-index: 100;
  }
  .modal-dismiss {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    border: 0;
    padding: 0;
    cursor: pointer;
  }
  .modal-card {
    position: relative;
    background: var(--bg, #fff);
    padding: 20px;
    border-radius: 8px;
    min-width: 360px;
    max-width: 90vw;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  }
  .modal-card h2 {
    margin: 0 0 12px;
  }
  .modal-card form label {
    display: block;
    margin-bottom: 12px;
  }
  .modal-card form label span {
    display: block;
    font-size: 12px;
    color: var(--muted);
    margin-bottom: 4px;
  }
  .modal-card form input,
  .modal-card form textarea {
    width: 100%;
    padding: 6px 8px;
    border: 1px solid var(--border);
    border-radius: 4px;
  }
  .modal-card .hint {
    color: var(--muted);
    font-size: 11px;
  }
  .modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 8px;
  }
  .error {
    color: var(--danger, #c83232);
    background: rgba(200, 50, 50, 0.06);
    padding: 8px;
    border-radius: 4px;
    margin: 8px 0;
  }
</style>
